import React from "react";
import { Typography, theme, Tag, Flex, Card, Divider } from "antd";
import {
  TagOutlined,
  BarcodeOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import type { ConceptData } from "../types";

const { Title, Text } = Typography;

interface ConceptDetailsProps {
  concept: ConceptData;
}

// ─── FieldItem ────────────────────────────────────────────────────────────────

const FieldItem = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => {
  const { token } = theme.useToken();
  return (
    <Flex vertical gap={3}>
      <Text
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: token.colorTextSecondary,
        }}
      >
        {label}
      </Text>
      {children}
    </Flex>
  );
};

// ─── SectionCard ─────────────────────────────────────────────────────────────

const SectionCard = ({
  icon,
  title,
  iconBg,
  iconColor,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  iconBg: string;
  iconColor: string;
  children: React.ReactNode;
}) => {
  const { token } = theme.useToken();
  return (
    <Card
      style={{ borderRadius: token.borderRadiusLG, height: "100%" }}
      styles={{ body: { padding: 24 } }}
      hoverable
    >
      <Flex vertical gap={18}>
        <Flex align="center" gap={12}>
          <Flex
            align="center"
            justify="center"
            style={{
              width: 40,
              height: 40,
              borderRadius: token.borderRadiusLG,
              background: iconBg,
              color: iconColor,
              fontSize: 18,
              flexShrink: 0,
            }}
          >
            {icon}
          </Flex>
          <Title level={5} style={{ margin: 0, fontWeight: 800, fontSize: 16 }}>
            {title}
          </Title>
        </Flex>
        <Divider style={{ margin: 0 }} />
        <Flex vertical gap={16}>
          {children}
        </Flex>
      </Flex>
    </Card>
  );
};

const ConceptDetails: React.FC<ConceptDetailsProps> = ({ concept }) => {
  const { token } = theme.useToken();

  return (
    <div style={{ padding: "0 8px 32px" }}>
      <Flex vertical gap={24}>
        {/* ── Profile Header ── */}
        <Flex vertical align="center" gap={10} style={{ textAlign: "center" }}>
          <Title
            level={2}
            style={{
              margin: 0,
              fontWeight: 900,
              fontSize: 26,
              letterSpacing: "-0.02em",
              color: token.colorText,
            }}
          >
            {concept.concept_name}
          </Title>

          <Tag
            color="blue"
            icon={<TagOutlined />}
            style={{
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              borderRadius: 999,
              margin: 0,
            }}
          >
            Concepto de Facturación
          </Tag>
        </Flex>

        {/* ── Info Card ── */}
        <div style={{ maxWidth: 600, margin: "0 auto", width: "100%" }}>
          <SectionCard
            icon={<InfoCircleOutlined />}
            title="Detalles del Concepto"
            iconBg={token.colorPrimaryBg}
            iconColor={token.colorPrimary}
          >
            <FieldItem label="Clave SAT">
              <Text
                strong
                copyable
                style={{
                  fontSize: 16,
                  fontWeight: 900,
                  color: token.colorText,
                  letterSpacing: "0.05em",
                }}
              >
                {concept.clave_sat}
              </Text>
            </FieldItem>

            <FieldItem label="Descripción">
              <Text
                style={{
                  color: token.colorText,
                  lineHeight: 1.6,
                  fontSize: 15,
                }}
              >
                {concept.description || "Sin descripción"}
              </Text>
            </FieldItem>
          </SectionCard>
        </div>
      </Flex>
    </div>
  );
};

export default ConceptDetails;

